// src/pages/Team/InviteMember.jsx
// STATUS: active | UPDATED: 2026-08-28
// LOG:
//   2026-08-28  New. The front of the invite flow.
//
// Invite somebody onto the floor.
//
// The role select only offers what this person is allowed to grant, because a
// dropdown that lists an option and then refuses it teaches people the product
// is unreliable. netlify/functions/invite-user.js checks the same rule against
// the caller's own profile row and is the one that actually decides.
//
// A platform admin also picks the store, because they belong to none. Everyone
// else invites into their own and is not asked, since being asked implies it
// could be answered differently.

import { useEffect, useState } from 'react'
import {
  Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, FormHelperText, Input, Select,
  VStack, Alert, AlertIcon, useDisclosure, useToast,
} from '@chakra-ui/react'
import { UserPlus } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { invitableRoles, roleLabel } from '../../lib/roles'

export default function InviteMember({ onInvited }) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { role, profile } = useAuth()
  const toast = useToast()

  const roles = invitableRoles(role)
  const isPlatformAdmin = role === 'admin' && !profile?.dealership_id

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [grant, setGrant] = useState(roles[roles.length - 1] || 'employee')
  // Only ever populated when granting platform admin, cleared the moment the
  // dialog closes, and never persisted anywhere.
  const [adminPassword, setAdminPassword] = useState('')
  const [dealershipId, setDealershipId] = useState('')
  const [stores, setStores] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // Only a platform admin needs the list, and only while the dialog is open.
  useEffect(() => {
    if (!isOpen || !isPlatformAdmin) return undefined
    let alive = true
    supabase
      .from('dealerships')
      .select('id, name')
      .order('name')
      .then(({ data, error: storeError }) => {
        if (!alive) return
        if (storeError) {
          setError('Could not load the dealership list.')
          return
        }
        setStores(data || [])
        if ((data || []).length === 1) setDealershipId(data[0].id)
      })
    return () => {
      alive = false
    }
  }, [isOpen, isPlatformAdmin])

  const reset = () => {
    setEmail('')
    setName('')
    setGrant(roles[roles.length - 1] || 'employee')
    setAdminPassword('')
    setError(null)
  }

  const close = () => {
    if (busy) return
    reset()
    onClose()
  }

  const submit = async (e) => {
    e.preventDefault()
    if (busy) return
    setError(null)
    setBusy(true)

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (!session?.access_token) {
        setError('Your session expired. Sign in again.')
        setBusy(false)
        return
      }

      const res = await fetch('/.netlify/functions/invite-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email,
          role: grant,
          password: grant === 'admin' ? adminPassword : undefined,
          displayName: name,
          ...(isPlatformAdmin ? { dealershipId } : {}),
        }),
      })

      if (res.status === 404) {
        setError('The invite function is not running. On localhost use `yarn dev`, which is netlify dev.')
        setBusy(false)
        return
      }

      const data = await res.json().catch(() => ({}))

      if (!res.ok) {
        setError(data.error || 'Could not send the invitation.')
        setBusy(false)
        return
      }

      toast({ title: data.message || `Invitation sent to ${email}.`, status: 'success', duration: 4000 })
      setBusy(false)
      reset()
      onClose()
      if (onInvited) onInvited()
    } catch {
      setError('Could not reach the server. Check your connection and try again.')
      setBusy(false)
    }
  }

  if (roles.length === 0) return null

  return (
    <>
      <Button size="sm" minH="40px" px={4} leftIcon={<UserPlus size={15} />} onClick={onOpen}>
        Invite someone
      </Button>

      <Modal isOpen={isOpen} onClose={close} isCentered motionPreset="slideInBottom">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent bg="card" mx={4}>
          <ModalHeader>Invite someone</ModalHeader>
          <ModalCloseButton isDisabled={busy} />
          <form onSubmit={submit}>
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                {error && (
                  <Alert status="error" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}

                <FormControl isRequired>
                  <FormLabel>Their email</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@dealership.com"
                    autoComplete="off"
                  />
                  <FormHelperText>They get a link to set a password and sign in.</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>Their first name</FormLabel>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Optional"
                    maxLength={60}
                    autoComplete="off"
                  />
                  <FormHelperText>They can change this themselves in Settings.</FormHelperText>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Role</FormLabel>
                  <Select value={grant} onChange={(e) => setGrant(e.target.value)}>
                    {roles.map((r) => (
                      <option key={r} value={r}>
                        {roleLabel(r)}
                      </option>
                    ))}
                  </Select>
                  <FormHelperText>
                    {grant === 'admin'
                      ? 'A platform admin sees every store in the system.'
                      : 'You can only grant a role below your own.'}
                  </FormHelperText>
                </FormControl>

                {/* The one action in this product that asks for a password
                    again. A live session proves somebody is signed in. It does
                    not prove they are the account holder, and creating an
                    account that sees every dealership is worth the extra step. */}
                {grant === 'admin' && (
                  <FormControl isRequired>
                    <FormLabel>Confirm your own password</FormLabel>
                    <Input
                      type="password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      autoComplete="current-password"
                      placeholder="Your password"
                    />
                    <FormHelperText>
                      Creating a platform admin needs your password again. It is checked and
                      never stored.
                    </FormHelperText>
                  </FormControl>
                )}

                {isPlatformAdmin && (
                  <FormControl isRequired>
                    <FormLabel>Dealership</FormLabel>
                    <Select
                      value={dealershipId}
                      onChange={(e) => setDealershipId(e.target.value)}
                      placeholder={stores.length ? 'Pick a store' : 'No dealerships yet'}
                      isDisabled={stores.length === 0}
                    >
                      {stores.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Select>
                    <FormHelperText>
                      Your account belongs to every store, so this one needs saying.
                    </FormHelperText>
                  </FormControl>
                )}
              </VStack>
            </ModalBody>

            <ModalFooter gap={3}>
              <Button variant="ghost" onClick={close} isDisabled={busy} minH="40px">
                Cancel
              </Button>
              <Button
                type="submit"
                minH="40px"
                isLoading={busy}
                loadingText="Sending"
                isDisabled={!email.trim() || (isPlatformAdmin && !dealershipId)}
              >
                Send invitation
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  )
}
